/**
 * Whatever hardware is in the room, turned into `XRIntentSet`.
 *
 * `xrintent.ts` is the contract and explains why it exists; this is the only
 * file that implements it, and the only file in the game that ever touches an
 * `XRInputSource`. Everything downstream — locomotion, interaction, the
 * in-world UI — reads intents and cannot tell which of the three producers
 * below filled them in.
 *
 * ## The three producers
 *
 *   controller   Quest. A pose every frame whether anybody is pressing
 *                anything or not, a thumbstick on `gamepad.axes`, a trigger on
 *                button 0, a grip on button 1, and four face buttons.
 *   pinch        Apple Vision Pro. The input source **is** the press: it is
 *                added on the pinch, carries a ray pointing where the player
 *                was looking at that instant, and is removed on release. No
 *                hover, no cursor, no stick, nothing at all between pinches.
 *                See `xrpinch.ts`, which is entirely about surviving that.
 *   hand         Either headset with tracked hands and no controllers. No
 *                buttons; a press is thumb tip to index tip under a threshold.
 *                See `XRHandPinch` in `xrhands.ts`.
 *
 * They are chosen per input source, not per session, because a session can have
 * one of each: a Quest player can put a controller down mid-run and pick their
 * hand up. `stickless` is the one thing decided for the whole session, and it
 * is sticky — a Quest that has shown a thumbstick once is not a stickless
 * device just because the controller is currently asleep on the sofa.
 *
 * ## Why the poses are read here rather than taken from three
 *
 * three's `WebXRManager` keeps its own array of controllers and assigns each
 * incoming input source to the first free entry. That is fine for two
 * controllers that connect once and stay. It is the wrong shape for a device
 * that adds and removes an input source on every single pinch, which is what
 * `inputsourceschange` does on visionOS — the assignment order stops being
 * stable, and a source can be dropped entirely if it arrives before three's
 * array has been populated. So the poses come straight from
 * `XRFrame.getPose(source.targetRaySpace, refSpace)` and the lifecycle is owned
 * here, where it can be made explicit. three's controller groups still exist
 * and still carry the visible hands and the torch; they are simply not the
 * authority on input.
 *
 * ## How the session drives it
 *
 * Everything needed is already on the renderer, so nothing has to be threaded
 * down from the frame loop and `game.ts` never learns any of this exists:
 *
 * ```ts
 * // once the session is running
 * this.inputs = makeXRInputs(session, this.rig);
 *
 * // at the top of every frame
 * const frame = renderer.xr.getFrame();
 * const refSpace = renderer.xr.getReferenceSpace();
 * if (frame && refSpace) this.inputs.update(frame, refSpace, dt);
 * ```
 *
 * `getFrame()` is null until the session's first animation frame has begun, so
 * the guard is not decoration.
 *
 * ## Allocation
 *
 * Nothing in this file allocates per frame. Slots, vectors, the `all` array and
 * every producer's scratch space are built once and updated in place; `all` is
 * emptied and refilled rather than rebuilt, so a pinch does not churn the heap.
 * The only per-frame garbage on this path is the `XRPose` the browser itself
 * returns from `getPose`, which the WebXR API gives no way to avoid.
 *
 * ## Not verified
 *
 * None of this has been run on a headset. The Quest path is a refactor of code
 * that worked; the Vision Pro path has never executed at all. See the batch
 * notes for what specifically needs a device.
 */

import * as THREE from 'three';
import type { InputSink } from '../core/input';
import { clamp } from '../core/math';
import type { ActionName } from '../core/types';
import { XRHandPinch } from './xrhands';
import type { XRIntent, XRIntentSet, XRIntentSource } from './xrintent';
import { XRPinchSelect } from './xrpinch';

/** Below this a stick is at rest. Generous: XR sticks drift. */
const STICK_DEADZONE = 0.18;

/** Below this a turn stick is at rest. */
const TURN_DEADZONE = 0.2;

/**
 * How many input sources can be tracked at once.
 *
 * Two hands is the real answer. Four is what is allocated, because a slot that
 * has lost its source still owes the frame that reports the release, and on a
 * device that adds and removes a source per pinch a fast second pinch can
 * arrive while the first is still being wound up. Four means the new one always
 * has somewhere to go.
 */
const MAX_SLOTS = 4;

/**
 * Seconds a tracked hand may be unreadable before its pinch is released.
 *
 * Hand tracking loses joints constantly — a hand turned edge-on, one hand
 * behind the other, a hand at the edge of the cameras' cone. Treating every
 * dropout as the player letting go would fire a teleport every time they turned
 * their wrist. Treating it as a hold forever would strand the action, so there
 * is a grace period and then a release. A third of a second is long enough to
 * cover an occlusion and short enough that nobody thinks the game has hung.
 */
const HAND_BLIND_GRACE = 0.35;

/**
 * Which actions the controllers are holding, so that only *changes* reach the
 * sink.
 *
 * This matters more than it looks. `InputSink.hold(a, true)` puts the action
 * into `justPressed` as well as into `down`, exactly as a key going down does —
 * so a source that re-asserted a held button every frame would make
 * `pressed()` true on every one of them, and a held X button would toggle the
 * flashlight sixty times a second. A button is polled here and edged here.
 */
export class XRHeldActions {
  private readonly held = new Set<ActionName>();

  set(sink: InputSink, action: ActionName, down: boolean): void {
    if (down === this.held.has(action)) return;
    if (down) this.held.add(action);
    else this.held.delete(action);
    sink.hold(action, down);
  }

  /**
   * Let go of everything. Called when a session ends: `hold` latches, so a
   * player who takes the headset off with the trigger down would otherwise go
   * back to the flat game permanently interacting.
   */
  releaseAll(sink: InputSink): void {
    for (const action of [...this.held]) this.set(sink, action, false);
    sink.axes(0, 0);
  }
}

/**
 * An intent, plus the parts of a controller that no other device has.
 *
 * `XRIntent` is deliberately the smallest shape both headsets can fill, so a
 * Quest's four face buttons and its handedness are not in it. They are still
 * worth having — the flashlight, the map, the inventory and the pause menu are
 * all bound to them, and pause in particular *has* to be, because the Quest
 * system button never reaches the page. So they ride along here, where a
 * consumer that wants them can read them and a consumer that does not is
 * unaffected: every one of them is false forever on a device without them, and
 * nothing in this game may require one.
 */
export interface XRHandIntent extends XRIntent {
  /** Which hand, where the runtime says. `none` is common and is not an error. */
  readonly handedness: 'left' | 'right' | 'none';
  /** The lower face button: X on the left controller, A on the right. */
  readonly lower: boolean;
  readonly lowerStarted: boolean;
  /** The upper face button: Y on the left controller, B on the right. */
  readonly upper: boolean;
  readonly upperStarted: boolean;
}

/** The set this file produces. `XRIntentSet` plus the handed extras. */
export interface XRInputs extends XRIntentSet {
  readonly primary: XRHandIntent;
  readonly all: readonly XRHandIntent[];
}

/** Deadzone and rescale, so the usable range starts where the stick leaves rest. */
function shape(v: number, dead: number): number {
  const m = Math.abs(v);
  if (m <= dead) return 0;
  return Math.sign(v) * Math.min(1, (m - dead) / (1 - dead));
}

/** A gamepad button that may not exist on this runtime's controller. */
function buttonDown(buttons: readonly GamepadButton[], index: number): boolean {
  const b = buttons[index];
  return !!b && b.pressed;
}

/**
 * One input source's worth of intent, and the machinery that keeps it honest.
 *
 * A slot outlives the source bound to it, which is the point of it being a slot
 * at all. When a transient pointer disappears the slot is unbound but stays
 * live for exactly one more frame so that `actionEnded` can be delivered — the
 * frame a teleport commits on. Only then does it go inert and become available
 * to the next source.
 */
class Slot implements XRHandIntent {
  /* -- XRIntent, read by everything downstream -- */
  source: XRIntentSource = 'none';
  ray: { origin: THREE.Vector3; direction: THREE.Vector3 } | null = null;
  acting = false;
  actionStarted = false;
  actionEnded = false;
  heldFor = 0;
  moveX = 0;
  moveZ = 0;
  turnX = 0;
  secondary = false;
  secondaryStarted = false;

  /* -- the handed extras -- */
  handedness: 'left' | 'right' | 'none' = 'none';
  lower = false;
  lowerStarted = false;
  upper = false;
  upperStarted = false;

  /* -- internals -- */

  /** The bound source, or null once it has gone. */
  src: XRInputSource | null = null;
  /**
   * The source this slot has just lost, kept for exactly one frame.
   *
   * Only so that the ray can be read one last time. A pinch that begins and
   * ends between two frames is delivered as a press — see `xrpinch.ts` — but if
   * its source had already been dropped there would be nowhere to aim it, and a
   * teleport with no destination is a press the player made and the game
   * silently ate. The pose usually still resolves on the frame the source was
   * removed, and when it does not this costs one failed lookup.
   */
  private goneSrc: XRInputSource | null = null;
  /** Which producer drives this slot. Survives `unbind` for the release frame. */
  kind: XRIntentSource = 'none';
  /** True while a controller in this slot is reporting a usable stick. */
  hasStick = false;
  /** In `all`, and therefore something a consumer can see. */
  live = false;
  /** Frame number of the most recent press, for picking `primary`. */
  lastActed = -1;

  private readonly rayOrigin = new THREE.Vector3();
  private readonly rayDir = new THREE.Vector3(0, 0, -1);
  /** The object handed out as `ray`. Never reallocated; never retained by anyone. */
  private readonly rayObj = { origin: this.rayOrigin, direction: this.rayDir };
  private hasRay = false;

  private readonly select = new XRPinchSelect();
  private readonly hand = new XRHandPinch();
  /** Seconds this hand's joints have been unreadable. */
  private blindFor = 0;

  private secondaryNow = false;
  private lowerNow = false;
  private upperNow = false;

  /**
   * True when nothing is bound here and nothing is still owed — no live intent
   * to keep reporting, and no press the frame loop has not seen yet.
   */
  isFree(): boolean {
    return this.src === null && !this.live && !this.select.pending();
  }

  /**
   * A `selectstart` arrived for the bound source. Only the select-driven
   * producers act on it: a controller polls its own trigger and a tracked hand
   * measures its own fingers, and counting the event as well would give either
   * of them two presses for one pull.
   */
  noteSelectStart(): void {
    if (this.kind === 'pinch') this.select.noteStart();
  }

  /** A `selectend` arrived for the bound source. */
  noteSelectEnd(): void {
    if (this.kind === 'pinch') this.select.noteEnd();
  }

  /**
   * Take an input source and decide which producer reads it.
   *
   * The order of the tests is load-bearing. `transient-pointer` is checked
   * first because on visionOS, with the hand-tracking permission granted, the
   * transient pointer *also* carries an `XRHand` — and the gaze ray it arrives
   * with is a far better thing to aim with than a pinch position derived from
   * joints. Checking `hand` first would quietly throw the gaze ray away on the
   * one device that has nothing else to aim with.
   */
  bind(src: XRInputSource): void {
    this.src = src;
    this.goneSrc = null;
    this.handedness = src.handedness === 'left' || src.handedness === 'right' ? src.handedness : 'none';

    const transient = src.targetRayMode === 'transient-pointer';
    const joints = !transient && src.hand ? src.hand : null;
    const pad = src.gamepad;
    if (transient) this.kind = 'pinch';
    else if (joints && this.hand.bind(joints)) this.kind = 'hand';
    else if (pad && pad.buttons.length > 0) this.kind = 'controller';
    // A source with no gamepad, no joints and no transient ray: a gaze-and-
    // select device, or a runtime that reports a controller it knows nothing
    // about. It has one button and no stick, which is the pinch shape.
    else this.kind = 'pinch';

    // Presence means "pressed" only for a transient pointer, where the source
    // existing is the pinch. For anything else presence is just presence, and a
    // hand that is always there would read as a button that is always down.
    this.select.reset(transient);
    this.hasStick = false;
    this.blindFor = 0;
    this.hasRay = false;
    this.secondaryNow = false;
    this.lowerNow = false;
    this.upperNow = false;
  }

  /**
   * The source has left the session. Deliberately does **not** clear `kind`,
   * `acting` or the ray: the next `update` still has to deliver `actionEnded`,
   * and it has to deliver it with the ray the player was aiming along, because
   * that is what the teleport commits to.
   */
  unbind(): void {
    if (this.src) this.goneSrc = this.src;
    this.src = null;
    this.hand.unbind();
    this.hasStick = false;
  }

  /** Wipe the slot completely. Only on session teardown. */
  clear(): void {
    this.unbind();
    this.goneSrc = null;
    this.kind = 'none';
    this.source = 'none';
    this.ray = null;
    this.hasRay = false;
    this.acting = false;
    this.actionStarted = false;
    this.actionEnded = false;
    this.heldFor = 0;
    this.moveX = 0;
    this.moveZ = 0;
    this.turnX = 0;
    this.secondary = false;
    this.secondaryStarted = false;
    this.lower = false;
    this.lowerStarted = false;
    this.upper = false;
    this.upperStarted = false;
    this.live = false;
    this.lastActed = -1;
    this.select.reset(false);
  }

  /**
   * One frame.
   *
   * `toWorld` maps the reference space the poses are asked in onto world space;
   * `suppressed` is the set telling this slot to stand down even though it still
   * has a source, which is how hand tracking gets out of the way of a device
   * that also does transient pointers.
   */
  update(
    frame: XRFrame,
    refSpace: XRReferenceSpace,
    toWorld: THREE.Matrix4,
    dt: number,
    frameNo: number,
    suppressed: boolean
  ): void {
    const src = suppressed ? null : this.src;
    const bound = src !== null;
    // One last look for a source that left since the previous frame. See
    // `goneSrc`: this is what gives a sub-frame pinch somewhere to point.
    const gone = this.goneSrc;
    this.goneSrc = null;
    if (!src && gone && !this.hasRay) {
      try {
        this.readRay(frame, refSpace, toWorld, gone);
      } catch {
        // A runtime is within its rights to refuse a space whose input source
        // has been collected. There was no ray before and there is none now.
      }
    }

    let want = false;
    if (src) {
      this.readRay(frame, refSpace, toWorld, src);
      if (this.kind === 'controller') want = this.pollController(src);
      else if (this.kind === 'hand') want = this.pollHand(frame, refSpace, dt);
      else want = this.select.resolve(true, this.acting);
    } else {
      this.moveX = 0;
      this.moveZ = 0;
      this.turnX = 0;
      this.lowerNow = false;
      this.upperNow = false;
      this.secondaryNow = false;
      // The select machine still runs with no source, because that is precisely
      // the frame a transient pointer's release has to be delivered on — and
      // because a pinch shorter than one frame is a press it has not played yet.
      want = this.kind === 'pinch' ? this.select.resolve(false, this.acting) : false;
    }

    this.actionStarted = want && !this.acting;
    this.actionEnded = !want && this.acting;
    this.acting = want;
    if (this.actionStarted) {
      // Zero on the frame of the press: the player has held it for no time yet,
      // so a hold-to-dash can test `heldFor >= interval` without the first hop
      // being a frame early. The press itself is `actionStarted`.
      this.heldFor = 0;
      this.lastActed = frameNo;
    } else if (want) {
      this.heldFor += dt;
    } else if (!this.actionEnded) {
      this.heldFor = 0;
    }
    // On the `actionEnded` frame `heldFor` deliberately keeps its final value,
    // so a consumer can tell a tap from a long hold at the moment of release
    // without having had to remember anything itself.

    const sec = bound && this.secondaryNow;
    this.secondaryStarted = sec && !this.secondary;
    this.secondary = sec;

    this.lowerStarted = this.lowerNow && !this.lower;
    this.lower = this.lowerNow;
    this.upperStarted = this.upperNow && !this.upper;
    this.upper = this.upperNow;

    this.live = bound || this.acting || this.actionEnded;
    this.source = this.live ? this.kind : 'none';
    if (!this.live) {
      this.ray = null;
      this.hasRay = false;
      this.heldFor = 0;
      this.blindFor = 0;
    } else if (this.hasRay) {
      this.ray = this.rayObj;
    }
  }

  /**
   * The target ray, in world space.
   *
   * A pose that fails to arrive leaves the previous ray standing rather than
   * nulling it. Tracking drops a frame here and there on every device, and on
   * Vision Pro the ray is only in existence for the length of a pinch — a hole
   * in the middle of one would otherwise take the teleport arc away and put it
   * back, which reads as the game glitching rather than as the hardware
   * blinking.
   */
  private readRay(frame: XRFrame, refSpace: XRReferenceSpace, toWorld: THREE.Matrix4, src: XRInputSource): void {
    const pose = frame.getPose(src.targetRaySpace, refSpace);
    if (!pose) return;
    const m = pose.transform.matrix;
    // Column-major: translation at 12..14, and the spec guarantees a target ray
    // runs along the space's -Z, which is the negated third basis vector.
    this.rayOrigin.set(m[12], m[13], m[14]).applyMatrix4(toWorld);
    this.rayDir.set(-m[8], -m[9], -m[10]).transformDirection(toWorld);
    this.hasRay = true;
  }

  /** A controller: everything is a poll. */
  private pollController(src: XRInputSource): boolean {
    const pad = src.gamepad;
    if (!pad) {
      // A controller whose gamepad went away underneath it. Rare, but a stick
      // left at its last reading would walk the player into a wall forever.
      this.moveX = 0;
      this.moveZ = 0;
      this.turnX = 0;
      this.hasStick = false;
      this.secondaryNow = false;
      this.lowerNow = false;
      this.upperNow = false;
      return false;
    }

    const axes = pad.axes;
    let rawX = 0;
    let rawY = 0;
    if (axes.length >= 4) {
      // `xr-standard`: 0 and 1 are the touchpad, 2 and 3 the thumbstick.
      rawX = axes[2];
      rawY = axes[3];
    } else if (axes.length >= 2) {
      // A controller with one two-axis input — an older 3DOF pad, or a runtime
      // that maps its only stick to the first pair.
      rawX = axes[0];
      rawY = axes[1];
    }
    this.hasStick = axes.length >= 2;
    // +y on an `xr-standard` stick is *back*, so it is negated into the game's
    // convention, where positive `moveZ` is forward.
    this.moveX = shape(rawX, STICK_DEADZONE);
    this.moveZ = shape(-rawY, STICK_DEADZONE);
    // The same axis again, deadzoned for turning rather than for walking. Which
    // hand turns and which hand walks is a comfort setting, so both are offered
    // and the consumer picks; see `readSticks`.
    this.turnX = shape(rawX, TURN_DEADZONE);

    const b = pad.buttons;
    this.secondaryNow = buttonDown(b, 1);
    this.lowerNow = buttonDown(b, 4);
    this.upperNow = buttonDown(b, 5);
    return buttonDown(b, 0);
  }

  /** A tracked hand: everything is a measurement. */
  private pollHand(frame: XRFrame, refSpace: XRReferenceSpace, dt: number): boolean {
    if (this.hand.read(frame, refSpace)) {
      this.blindFor = 0;
      this.secondaryNow = this.hand.secondary;
      return this.hand.primary;
    }
    // Joints unreadable. Hold what the hand was doing; see `HAND_BLIND_GRACE`.
    this.blindFor += dt;
    if (this.blindFor <= HAND_BLIND_GRACE) return this.acting;
    this.secondaryNow = false;
    return false;
  }
}

class Inputs implements XRInputs {
  /** Live slots only, refilled in place. Never retained by a consumer. */
  readonly all: XRHandIntent[] = [];

  private readonly session: XRSession;
  /**
   * The object whose world matrix maps the session's reference space onto the
   * game's world — `render/xr.ts`'s rig. Held rather than passed per frame
   * because `XRIntentSet.update` is a fixed signature and the rig does not
   * change for the life of a session.
   */
  private readonly space: THREE.Object3D;
  private readonly slots: Slot[] = [];
  /** Handed out as `primary` when there is nothing at all. Always inert. */
  private readonly idle = new Slot();
  private primarySlot: Slot;

  /** Sticky: a session that has ever shown a thumbstick is not a stickless one. */
  private sawStick = false;
  /**
   * Sticky: this device does transient pointers, so any hand-tracking source it
   * also reports is a duplicate of the same fingers and must not fire twice.
   * Sticky because a transient pointer only exists during a pinch — testing it
   * live would switch producers between every pinch and the next.
   */
  private sawTransient = false;
  /** A source arrived with no free slot. Try again next frame. */
  private pendingBind = false;
  private frameNo = 0;
  private disposed = false;

  constructor(session: XRSession, space: THREE.Object3D) {
    this.session = session;
    this.space = space;
    for (let i = 0; i < MAX_SLOTS; i++) this.slots.push(new Slot());
    this.primarySlot = this.idle;

    // Sources are bound the moment the session says they exist, rather than at
    // the top of the next frame, because `selectstart` follows
    // `inputsourceschange` within the same frame and has to find a slot to land
    // on. A press that arrives before its source has been bound is a pinch the
    // player made and the game ignored.
    session.addEventListener('inputsourceschange', this.onSourcesChanged);
    session.addEventListener('selectstart', this.onSelectStart);
    session.addEventListener('selectend', this.onSelectEnd);
    this.syncSources();
  }

  get primary(): XRHandIntent {
    return this.primarySlot;
  }

  get stickless(): boolean {
    return !this.sawStick;
  }

  update(frame: XRFrame, refSpace: XRReferenceSpace, dt: number): void {
    if (this.disposed) return;
    if (this.pendingBind) this.syncSources();

    // The rig's world matrix is only recomposed inside `render()`, and this runs
    // at the top of the frame — so it is composed here, from the rig and its
    // parents but not its children, which is both correct and cheap.
    this.space.updateWorldMatrix(true, false);
    const toWorld = this.space.matrixWorld;

    let changed = false;
    let stick = false;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      const was = slot.live;
      const stand = this.sawTransient && slot.kind === 'hand';
      slot.update(frame, refSpace, toWorld, dt, this.frameNo, stand);
      // A hand that has stood down for a device which also reports transient
      // pointers hands its slot back, once it has finished releasing, rather
      // than holding one inert for the rest of the session.
      if (stand && !slot.live) slot.unbind();
      if (slot.live !== was) changed = true;
      if (slot.hasStick) stick = true;
    }
    if (stick) this.sawStick = true;
    if (changed) this.rebuildAll();
    this.pickPrimary();
    this.frameNo++;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.session.removeEventListener('inputsourceschange', this.onSourcesChanged);
    this.session.removeEventListener('selectstart', this.onSelectStart);
    this.session.removeEventListener('selectend', this.onSelectEnd);
    for (let i = 0; i < this.slots.length; i++) this.slots[i]!.clear();
    this.all.length = 0;
    this.primarySlot = this.idle;
  }

  /* ---------------------------------------------------------------- *
   * Source lifecycle
   * ---------------------------------------------------------------- */

  private onSourcesChanged = (): void => {
    // The event carries `added` and `removed`, and they are not used: a full
    // reconciliation against `session.inputSources` is a scan of at most a
    // handful of entries and cannot drift, whereas trusting the deltas means
    // trusting that none was ever missed across a whole session of a device
    // that fires this event on every pinch.
    this.syncSources();
  };

  private onSelectStart = (e: XRInputSourceEvent): void => {
    this.slotFor(e.inputSource)?.noteSelectStart();
  };

  private onSelectEnd = (e: XRInputSourceEvent): void => {
    this.slotFor(e.inputSource)?.noteSelectEnd();
  };

  /** The slot holding a source, or null. At most four comparisons. */
  private slotFor(src: XRInputSource): Slot | null {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      if (slot.src === src) return slot;
    }
    return null;
  }

  private syncSources(): void {
    if (this.disposed) return;
    const list = this.session.inputSources;

    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      if (slot.src && !contains(list, slot.src)) slot.unbind();
    }

    this.pendingBind = false;
    for (let i = 0; i < list.length; i++) {
      const src = list[i]!;
      if (this.slotFor(src)) continue;
      // Hand-tracking sources on a device that also does transient pointers are
      // the same fingers reported twice, and a pinch that fired both would
      // teleport twice. The transient pointer wins: it is the one that carries
      // a gaze ray, which is the only thing on such a device worth aiming with.
      if (this.sawTransient && src.targetRayMode !== 'transient-pointer' && src.hand) continue;
      const slot = this.freeSlot();
      if (!slot) {
        // Every slot is either bound or still owes a release. Next frame there
        // will be room; a transient pointer that has to wait one frame is still
        // caught, because its press is delivered by presence rather than by the
        // `selectstart` that was missed.
        this.pendingBind = true;
        continue;
      }
      slot.bind(src);
      if (src.targetRayMode === 'transient-pointer') this.sawTransient = true;
    }
  }

  private freeSlot(): Slot | null {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      if (slot.isFree()) return slot;
    }
    return null;
  }

  /** Emptied and refilled, so membership changing does not allocate an array. */
  private rebuildAll(): void {
    this.all.length = 0;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      if (slot.live) this.all.push(slot);
    }
  }

  /**
   * Whichever hand acted most recently.
   *
   * Most recently *pressed*, not most recently seen, because that is the
   * question a one-handed consumer is really asking: which of these is the
   * player using. A hand that has never pressed anything can still be primary
   * when nothing else has either, so a Quest player who has picked a controller
   * up but not yet pulled the trigger still gets a pointer.
   */
  private pickPrimary(): void {
    let best: Slot | null = null;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      if (!slot.live) continue;
      if (!best || slot.lastActed > best.lastActed) {
        best = slot;
        continue;
      }
      // A tie is two sources that have never acted, or two that acted on the
      // same frame. The one actually being held wins.
      if (slot.lastActed === best.lastActed && slot.acting && !best.acting) best = slot;
    }
    this.primarySlot = best ?? this.idle;
  }
}

/** Whether a source is still in the session's live list. */
function contains(list: XRInputSourceArray, src: XRInputSource): boolean {
  for (let i = 0; i < list.length; i++) {
    if (list[i] === src) return true;
  }
  return false;
}

/**
 * Build the intent producer for a session.
 *
 * `space` is the object whose world transform maps the session's reference
 * space onto the game's world — the rig in `render/xr.ts`. Rays come out in
 * world space, as `xrintent.ts` promises, and nothing downstream has to know
 * that a reference space exists.
 *
 * Built once per session and disposed with it. Constructing it attaches three
 * session listeners and reads `session.inputSources` once; it does nothing at
 * all on a machine that never starts a session, because nothing ever calls it.
 */
export function makeXRInputs(session: XRSession, space: THREE.Object3D): XRInputs {
  return new Inputs(session, space);
}

/* ------------------------------------------------------------------ *
 * Turning intents into the things the flat game already understands
 * ------------------------------------------------------------------ */

/** What one frame of stick reading asked for, beyond the actions. */
export interface XRAxes {
  /** -1..1 strafe, +right. */
  moveX: number;
  /** -1..1 forward, +forward. Matches `InputAPI.moveZ`. */
  moveZ: number;
  /** -1..1 turn request, +right. Shaped into a turn by `xrlocomotion.ts`. */
  turn: number;
}

/**
 * Collapse every hand's sticks into one move and one turn.
 *
 * `moveHand` comes from the comfort settings; a left-handed player swaps it. A
 * source that reports no handedness is treated as the move hand, which is the
 * right way round — it keeps walking, and loses only a turn it could not have
 * placed on a specific hand anyway.
 *
 * All zeroes on a stickless device, every frame, by construction: `moveX`,
 * `moveZ` and `turnX` are only ever written by the controller producer.
 */
export function readSticks(inputs: XRInputs, moveHand: 'left' | 'right', out: XRAxes): XRAxes {
  out.moveX = 0;
  out.moveZ = 0;
  out.turn = 0;
  const all = inputs.all;
  for (let i = 0; i < all.length; i++) {
    const h = all[i]!;
    if (h.handedness === moveHand || h.handedness === 'none') {
      out.moveX += h.moveX;
      out.moveZ += h.moveZ;
    } else {
      out.turn += h.turnX;
    }
  }
  out.moveX = clamp(out.moveX, -1, 1);
  out.moveZ = clamp(out.moveZ, -1, 1);
  out.turn = clamp(out.turn, -1, 1);
  return out;
}

/**
 * Push the button half of the intents into the game's action sink.
 *
 * The map, unchanged from the version that shipped:
 *
 *   either trigger / pinch   interact, but only when `primaryIsInteract`
 *   left squeeze             sprint   (held; a fast run in stereo is worth it)
 *   right squeeze            attack   (the wind-up; it is already a hold)
 *   X (left lower)           flashlight
 *   Y (left upper)           map
 *   A (right lower)          inventory
 *   B (right upper)          pause
 *
 * `pause` has to be one of these. The menu button on a Quest controller belongs
 * to the system and never reaches the page, so a player with no binding for it
 * would have to take the headset off to reach a menu.
 *
 * `primaryIsInteract` exists because teleport locomotion now wants the same
 * press. It is a boolean rather than an action name so that it can be written
 * every frame either way: an action that was latched down and then stopped
 * being written would stay down in the flat game forever, which is the exact
 * failure `XRHeldActions` exists to prevent.
 *
 * Crouch is deliberately absent: in a headset you crouch by crouching, and
 * `xr.ts` reports it from the height of the player's own head. So is `look`: an
 * artificial turn goes to the rig, and nothing downstream should be able to
 * mistake it for the player having moved their own head.
 */
export function applyIntentActions(
  inputs: XRInputs,
  sink: InputSink,
  held: XRHeldActions,
  primaryIsInteract: boolean
): void {
  let primary = false;
  let sprint = false;
  let attack = false;
  let flashlight = false;
  let map = false;
  let inventory = false;
  let pause = false;

  const all = inputs.all;
  for (let i = 0; i < all.length; i++) {
    const h = all[i]!;
    primary = primary || h.acting;
    if (h.handedness === 'right') {
      attack = attack || h.secondary;
      inventory = inventory || h.lower;
      pause = pause || h.upper;
    } else {
      // A runtime that reports no handedness falls in here, which is the right
      // way round: it keeps the torch and the map, and loses only the pause it
      // could not have placed anywhere sensible anyway.
      sprint = sprint || h.secondary;
      flashlight = flashlight || h.lower;
      map = map || h.upper;
    }
  }

  held.set(sink, 'interact', primaryIsInteract && primary);
  held.set(sink, 'sprint', sprint);
  held.set(sink, 'attack', attack);
  held.set(sink, 'flashlight', flashlight);
  held.set(sink, 'map', map);
  held.set(sink, 'inventory', inventory);
  held.set(sink, 'pause', pause);
}
